import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import type { Benefit } from '@yp/types';
import { useAuth } from '../context/AuthContext';
import { getAdminBenefits, deleteBenefit } from '../lib/api';

export default function BenefitsPage() {
  const { accessToken } = useAuth();
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  async function fetchBenefits() {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      const data = await getAdminBenefits(accessToken);
      setBenefits(data.sort((a, b) => a.sort_order - b.sort_order));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load benefits');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchBenefits();
  }, [accessToken]);

  async function handleDelete(id: string, title: string) {
    if (!accessToken) return;
    if (!confirm(`Delete benefit "${title}"? This cannot be undone.`)) return;
    try {
      await deleteBenefit(accessToken, id);
      setBenefits((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-100">Benefits</h1>
          <p className="mt-0.5 text-sm text-gray-500">{benefits.length} benefit cards — ditampilkan di homepage</p>
        </div>
        <Link
          to="/benefits/new"
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white"
          style={{ backgroundColor: 'var(--yp-pink)' }}
        >
          <Plus size={16} />
          Add Benefit
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-pink-500 border-t-transparent" />
        </div>
      ) : error ? (
        <p className="rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">{error}</p>
      ) : benefits.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center">
          <p className="text-gray-500">No benefits yet.</p>
          <Link to="/benefits/new" className="mt-2 inline-block text-sm text-pink-400 hover:underline">
            Add the first benefit
          </Link>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)]">
                <th className="w-8 px-3 py-3 text-left text-[11px] font-medium tracking-wider text-gray-500" />
                <th className="px-4 py-3 text-left text-[11px] font-medium tracking-wider text-gray-500">ORDER</th>
                <th className="px-4 py-3 text-left text-[11px] font-medium tracking-wider text-gray-500">TITLE (ID)</th>
                <th className="hidden px-4 py-3 text-left text-[11px] font-medium tracking-wider text-gray-500 md:table-cell">TITLE (JP)</th>
                <th className="px-4 py-3 text-left text-[11px] font-medium tracking-wider text-gray-500">STATUS</th>
                <th className="px-4 py-3 text-right text-[11px] font-medium tracking-wider text-gray-500">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {benefits.map((benefit) => (
                <tr key={benefit.id} className="bg-[var(--color-background)] hover:bg-white/[0.02] transition-colors">
                  <td className="px-3 py-3">
                    <GripVertical size={14} className="text-gray-600" />
                  </td>
                  <td className="px-4 py-3 text-gray-500 tabular-nums">
                    {benefit.sort_order}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {benefit.icon_url && (
                        <img src={benefit.icon_url} alt="" className="h-7 w-7 object-contain" />
                      )}
                      <span className="font-medium text-gray-200">{benefit.title_id}</span>
                    </div>
                  </td>
                  <td className="hidden px-4 py-3 text-gray-400 md:table-cell">
                    {benefit.title_jp}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        benefit.is_active
                          ? 'bg-green-500/15 text-green-400'
                          : 'bg-gray-500/15 text-gray-500'
                      }`}
                    >
                      {benefit.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/benefits/${benefit.id}/edit`}
                        className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs text-gray-400 transition-colors hover:border-pink-500/30 hover:text-pink-400 flex items-center gap-1.5"
                      >
                        <Pencil size={12} />
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(benefit.id, benefit.title_id)}
                        className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs text-gray-400 transition-colors hover:border-red-500/30 hover:text-red-400 flex items-center gap-1.5"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
